function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { useDrag } from "@use-gesture/react";
import styled from "styled-components";
import React, { memo, useCallback, useEffect, useRef } from "react";
import useEvent from "react-use/lib/useEvent";
import clamp from "astrid-helpers/src/clamp";
import map from "astrid-helpers/src/map";
import { mapLinearLog, mapLogLinear } from "astrid-helpers/src/mapLog";
import { dividerColor } from "../../../helpers/color";
import { getDuration } from "./state/duration";
import { getOffset, setOffset } from "./state/offset";
import { getRect, setRect } from "./state/rect";
import { getSize, setSize, useSize } from "./state/size";
const threshold = 5;
const sensitivity = 160;
const Root = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Root",
  componentId: "sc-1jmmfc5-0"
})(["position:relative;margin-top:24px;width:100%;flex:1;touch-action:none;border-top:2px solid ", ";background-image:repeating-linear-gradient( 45deg,transparent,transparent 6px,", " 6px,", " 8px );&:focus{outline:none;}"], dividerColor, dividerColor, dividerColor));
const Timeline = /*#__PURE__*/memo(({
  min,
  max,
  onMove,
  onClick,
  onDoubleClick,
  ...props
}) => {
  const ref = useRef();
  const level = useRef(0);
  const moving = useRef();
  const startTime = useRef(null);
  const startSize = useRef();
  const startOffset = useRef();
  const isDoubleClicking = useRef();
  const size = useSize();
  const onDrag = useDrag(useCallback(({
    xy,
    tap,
    axis,
    last,
    first,
    event,
    delta,
    initial,
    movement,
    timeStamp
  }) => {
    const [x, y] = xy;
    const [deltaX, deltaY] = delta;
    const [initialX, initialY] = initial;
    event.stopPropagation();
    if (tap) {
      isDoubleClicking.current = timeStamp - startTime.current < 500;
      startTime.current = timeStamp;
      const {
        left,
        width
      } = getRect();
      const mousePosition = map(x - left, 0, width, 0, getSize());
      const position = clamp(getOffset() + mousePosition, 0, getDuration());
      if (isDoubleClicking.current) {
        onDoubleClick?.(position);
      } else {
        onClick?.(position, ref.current);
      }
    } else if (first) {
      startSize.current = getSize();
      startOffset.current = getOffset();
    } else if (last) {
      if (moving.current) {
        setSize(getSize());
        setOffset(getOffset());
      }
      moving.current = false;
    } else {
      const [x] = movement;
      const {
        left,
        width
      } = getRect();
      if (!moving.current) {
        moving.current = true;
        onMove && onMove();
      }
      if (axis === "x") {
        const size = getSize();
        const duration = getDuration();
        setOffset(clamp(startOffset.current + map(-x, 0, width, 0, size), 0, duration - size));
      } else if (axis === "y") {
        level.current = clamp(level.current - deltaY, -sensitivity, sensitivity);
        const tempSize = mapLinearLog(level.current, -sensitivity, sensitivity, min, max);
        const tempOffset = Math.max(0, startOffset.current + (startSize.current - tempSize) * ((initialX - left) / width));
        setSize(tempSize);
        setOffset(tempOffset);
      }
    }
  }, [max, min, onMove, onClick, onDoubleClick]), {
    axis: "lock",
    filterTaps: true,
    axisThreshold: {
      mouse: threshold,
      touch: threshold,
      pen: threshold
    }
  });
  const onResize = useCallback(() => {
    const {
      top,
      left,
      width,
      height
    } = ref.current.getBoundingClientRect();
    setRect({
      top: top + window.scrollY,
      left: left + window.scrollX,
      width,
      height
    });
  }, []);
  useEffect(() => {
    level.current = mapLogLinear(size, min, max, -sensitivity, sensitivity);
  }, [max, min, size]);
  useEffect(() => {
    onResize();
  }, [onResize]);
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, []);
  useEvent("resize", onResize);
  return /*#__PURE__*/React.createElement(Root, _extends({
    ref: ref
  }, onDrag(), props));
});
Timeline.defaultProps = {
  min: 1,
  max: 1000 * 60 * 3
};
export default Timeline;
//# sourceMappingURL=Timeline.js.map